import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { messagePreviewText, timeAgo, Tooltip } from '@odo/shared-ui';
import {
    CONVERSATION_STATUS_COLORS,
    CONVERSATION_STATUS_LABELS
} from '../conversations/ConversationStatusBadge';
import { Avatar } from '../Avatar';
import { PracticeDoctorLabel } from '../shared/PracticeDoctorLabel';
import { buildConversationsUrl } from '../../utils/conversationsLink';
import { ASSIGNEE_FILTER_ME, ASSIGNEE_FILTER_UNASSIGNED } from '../../utils/assigneeFilter';
import { Link } from 'react-router-dom';
import {
    conversationRowKey,
    type ConversationRow,
    type ConversationStatus
} from '../../services/conversations';

// Unread, unscoped by assignee: the card mixes mine with unclaimed team rows,
// so pinning Me here would send "View all" to a subset of what it shows.
export const MY_CONVERSATIONS_URL = buildConversationsUrl({ filter: 'unread' });

const SKELETON_ROWS = ['s1', 's2', 's3', 's4', 's5'];

// The list badge at card scale — shares ConversationStatusBadge's colours and
// labels so the two surfaces can't drift, but drops its table-cell sizing.
function ThreadBadge({ status }: { status: ConversationStatus }) {
    return (
        <span
            className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded"
            style={{ backgroundColor: CONVERSATION_STATUS_COLORS[status], color: '#fff' }}
        >
            {CONVERSATION_STATUS_LABELS[status]}
        </span>
    );
}

function ConversationCardRow({ row }: { row: ConversationRow }) {
    const preview = row.lastMessageText
        ? messagePreviewText(row.lastMessageText)
        : 'No messages yet';

    return (
        <Link
            to={buildConversationsUrl({
                patientId: row.patientId,
                // A row is (patientId, status), so the link carries the thread
                // it came from rather than defaulting to the external one.
                status: row.status,
                // An unclaimed row lands on the view it came from: what nobody
                // owns plus what is mine, scoped to that conversation's team.
                assignee: row.assignedTo
                    ? ASSIGNEE_FILTER_ME
                    : [ASSIGNEE_FILTER_UNASSIGNED, ASSIGNEE_FILTER_ME],
                team: row.assignedTo ? undefined : (row.team ?? undefined),
                filter: 'unread'
            })}
            className="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0"
        >
            <div className="w-8 h-8 flex-shrink-0">
                <Avatar
                    user={{ name: row.name, email: '' }}
                    thumbnailURL={row.profileThumbnailUrl ?? undefined}
                />
            </div>
            <div className="flex-1 min-w-0 flex flex-col leading-tight">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate text-[13px] font-semibold text-gray-800">
                        {row.name}
                    </span>
                    {row.practiceName && (
                        <span className="truncate text-[11px] text-gray-400">
                            ·{' '}
                            <PracticeDoctorLabel
                                practiceName={row.practiceName}
                                format="practice-only"
                            />
                        </span>
                    )}
                    {!row.assignedTo && (
                        <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                            Unassigned
                        </span>
                    )}
                </div>
                <Tooltip content={preview}>
                    <span className="truncate text-[11px] text-gray-500 mt-0.5">{preview}</span>
                </Tooltip>
            </div>
            <span className="flex-shrink-0 text-[11px] text-gray-400 tabular-nums">
                {timeAgo(row.lastMessageDate, '')}
            </span>
            <ThreadBadge status={row.status} />
        </Link>
    );
}

interface MyConversationsListProps {
    conversations: ConversationRow[];
    isLoading: boolean;
}

// Unread only, newest first: mine plus unclaimed on my team, one flat list.
// Read-only — every row deep-links into the Conversations page.
export function MyConversationsList({ conversations, isLoading }: MyConversationsListProps) {
    if (isLoading) {
        return (
            <div className="flex flex-col">
                {SKELETON_ROWS.map((key) => (
                    <div key={key} className="flex items-center gap-3 px-5 py-2.5">
                        <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse flex-shrink-0 ml-3" />
                        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                            <div className="h-3 w-1/3 bg-gray-200 rounded animate-pulse" />
                            <div className="h-2.5 w-1/2 bg-gray-100 rounded animate-pulse" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (conversations.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center gap-2 text-center px-6 py-16">
                <ChatBubbleLeftRightIcon className="h-8 w-8 text-gray-300" strokeWidth={1} />
                <p className="text-sm text-gray-400">Nothing unread for you or your team.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            {conversations.map((row) => (
                <ConversationCardRow key={conversationRowKey(row)} row={row} />
            ))}
        </div>
    );
}
